import type { TFunction } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { toEpochMillis } from '@/lib/format'

import type {
  AgentSettingsFeatureError,
  AgentSettingsScope,
  AgentSettingsVersion,
  EffectiveAgentSettings,
  ReviewPolicySettings,
} from './types'

/**
 * The ONE EN/NL mapping from server identifiers to user copy for the agent
 * library, configuration and guardrails surfaces (W7.7 owns it, W7.8 consumes
 * it). Raw wire values (rungs, provenance, scopes, refusal codes, server
 * message keys, epoch/ISO timestamps) never reach the screen directly: an
 * unknown value falls back to a neutral label, never to the identifier.
 */

const RUNGS = new Set([
  'L1_SUGGESTS',
  'L2_DRAFTS',
  'L3_GUARDED_ACT',
  'L4_ACTS_AND_REPORTS',
  'SYSTEM_DEFAULT',
])
const PROVENANCES = new Set([
  'FIRM',
  'VERTICAL',
  'CLIENT',
  'PROJECT_PHASE',
  'SYSTEM_DEFAULT',
  'INHERITED',
])
const SCOPES = new Set(['FIRM', 'VERTICAL', 'CLIENT', 'PROJECT_PHASE'])
const PERMISSION_MODES = new Set([
  'allowed_internal',
  'requires_locked_scope',
  'refused',
  'runtime_classification',
])
const OPERATIONS = new Set(['read', 'write', 'message'])
const TARGETS = new Set(['internal', 'client_sandbox', 'client_production'])
const OUTPUT_PERMISSIONS = new Set(['client-read', 'client-write', 'message'])
const REFUSALS = new Set([
  'unknown-classification',
  'client-production-write-refused',
  'client-message-refused',
  'scope-unresolved',
  'scope-read-failed',
  'connector-not-in-scope',
])
const DIFF_FIELDS = new Set([
  'autonomyRung',
  'costCeilingPerRunMinor',
  'costCeilingPerPhaseMinor',
  'currency',
  'policyPresetKey',
  'clientOverrideReason',
  'outputPermissions',
  'reviewPolicy.secondReviewerRule',
  'reviewPolicy.escalationHours',
])

/** Server message keys the UI translates; everything else keeps the fallback. */
const SERVER_MESSAGES: Readonly<Record<string, string>> = {
  'agent-settings.client-override.reason.required':
    'clientOverrideReasonRequired',
  'agent-settings.currency.required': 'currencyRequired',
  'agent-settings.currency.invalid': 'currencyInvalid',
  'agent-settings.cost-ceiling.negative': 'costCeilingNegative',
  'agent-settings.write.forbidden': 'writeForbidden',
  'agent-settings.conflict': 'conflict',
  'agent-settings.approver.same-principal': 'samePrincipal',
  'agent-settings.payload.stale': 'payloadStale',
  'agent-settings.draft.not-found': 'draftNotFound',
  'agent-settings.publish.no-change': 'noChange',
  'agent-settings.output-permission.unknown': 'outputPermissionUnknown',
  'agent-settings.output-permission.refused': 'outputPermissionRefused',
  'agent-settings.invalid': 'invalid',
}

/** The server key a refusal carries: its code, else a key-shaped message prefix. */
export const serverKeyOf = (
  error: Pick<AgentSettingsFeatureError, 'code' | 'message'> | null
): string | null => {
  if (error === null) return null
  if (error.code !== undefined && error.code in SERVER_MESSAGES)
    return error.code
  const prefix = error.message.split(':')[0]?.trim() ?? ''
  return prefix in SERVER_MESSAGES ? prefix : (error.code ?? null)
}

const pick = (
  t: TFunction,
  set: ReadonlySet<string>,
  group: string,
  value: string | null | undefined
): string =>
  value !== null && value !== undefined && set.has(value)
    ? t(`labels.${group}.${value}`)
    : t('labels.unknown')

export type EffectiveSource =
  | { kind: 'unavailable' }
  | { kind: 'system-default' }
  | { kind: 'published'; version: number }
  | { kind: 'inherited'; from: string }

/**
 * Where the effective view really comes from, read from its provenance and the
 * rung's own versions: never "published" unless a published version is in
 * force at this scope.
 */
export const effectiveSourceOf = (
  effective: EffectiveAgentSettings | null,
  versions: readonly AgentSettingsVersion[],
  scope: AgentSettingsScope
): EffectiveSource => {
  if (effective === null) return { kind: 'unavailable' }
  const sources = Object.values(effective.provenance)
  if (
    sources.length === 0 ||
    sources.every((source) => source === 'SYSTEM_DEFAULT')
  )
    return { kind: 'system-default' }
  const latest = versions.reduce<AgentSettingsVersion | null>(
    (best, version) =>
      best === null || version.version > best.version ? version : best,
    null
  )
  const rungSource = effective.provenance.autonomyRung
  if (latest !== null && (rungSource === undefined || rungSource === scope))
    return { kind: 'published', version: latest.version }
  return { kind: 'inherited', from: rungSource ?? 'SYSTEM_DEFAULT' }
}

export interface AgentSettingsLabels {
  rung: (value: string | null | undefined) => string
  provenance: (value: string | null | undefined) => string
  scope: (value: AgentSettingsScope | string | null | undefined) => string
  permissionMode: (value: string | null | undefined) => string
  operation: (value: string | null | undefined) => string
  target: (value: string | null | undefined) => string
  outputPermission: (value: string | null | undefined) => string
  refusal: (value: string | null | undefined) => string
  diffField: (field: string) => string
  diffValue: (field: string, value: string | null) => string | null
  reviewPolicy: (policy: ReviewPolicySettings | null | undefined) => string
  dateTime: (value: unknown) => string | null
  serverMessage: (error: AgentSettingsFeatureError | null) => string | null
  effectiveSource: (
    effective: EffectiveAgentSettings | null,
    versions: readonly AgentSettingsVersion[],
    scope: AgentSettingsScope
  ) => string
}

/** Pure builder, so the mapping is unit-testable without React. */
export const agentSettingsLabels = (
  t: TFunction,
  language: string
): AgentSettingsLabels => {
  const rung = (value: string | null | undefined) =>
    pick(t, RUNGS, 'rung', value)
  const provenance = (value: string | null | undefined) =>
    pick(t, PROVENANCES, 'provenance', value)
  const outputPermission = (value: string | null | undefined) =>
    pick(t, OUTPUT_PERMISSIONS, 'outputPermission', value)
  const reviewPolicy = (policy: ReviewPolicySettings | null | undefined) => {
    if (policy === null || policy === undefined)
      return t('labels.reviewPolicy.inherited')
    const parts = [t('labels.reviewPolicy.namedHumanMerge')]
    if (policy.secondReviewerRule !== undefined && policy.secondReviewerRule !== null)
      parts.push(
        t('labels.reviewPolicy.secondReviewer', {
          rule: policy.secondReviewerRule,
        })
      )
    if (policy.escalationHours !== undefined && policy.escalationHours !== null)
      parts.push(
        t('labels.reviewPolicy.escalation', { count: policy.escalationHours })
      )
    return parts.join(' · ')
  }
  const dateTime = (value: unknown): string | null => {
    const millis = toEpochMillis(value)
    if (millis === null) return null
    return new Intl.DateTimeFormat(language === 'nl' ? 'nl-NL' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(millis))
  }
  return {
    rung,
    provenance,
    scope: (value) => pick(t, SCOPES, 'scope', value),
    permissionMode: (value) =>
      pick(t, PERMISSION_MODES, 'permissionMode', value),
    operation: (value) => pick(t, OPERATIONS, 'operation', value),
    target: (value) => pick(t, TARGETS, 'target', value),
    outputPermission,
    refusal: (value) => pick(t, REFUSALS, 'refusal', value),
    diffField: (field) =>
      DIFF_FIELDS.has(field)
        ? t(`labels.diffField.${field}`)
        : t('labels.unknown'),
    diffValue: (field, value) => {
      if (value === null) return null
      if (field === 'autonomyRung') return rung(value)
      if (field === 'outputPermissions')
        return value
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '')
          .map(outputPermission)
          .join(', ')
      return value
    },
    reviewPolicy,
    dateTime,
    serverMessage: (error) => {
      if (error === null) return null
      const key = serverKeyOf(error)
      if (key !== null && key in SERVER_MESSAGES)
        return t(`labels.serverMessage.${SERVER_MESSAGES[key]}`)
      // An unmapped key-shaped message is an identifier, not copy.
      return /^[a-z0-9-]+(\.[a-z0-9-]+)+/.test(error.message)
        ? t('labels.serverMessage.invalid')
        : error.message
    },
    effectiveSource: (effective, versions, scope) => {
      const source = effectiveSourceOf(effective, versions, scope)
      switch (source.kind) {
        case 'unavailable':
          return t('catalogue.effectiveUnavailable')
        case 'system-default':
          return t('labels.effective.systemDefault')
        case 'published':
          return t('labels.effective.published', { version: source.version })
        case 'inherited':
          return t('labels.effective.from', {
            source: provenance(source.from),
          })
      }
    },
  }
}

/** Hook form: one memoised mapping per language. */
export function useAgentSettingsLabels(): AgentSettingsLabels {
  const { t, i18n } = useTranslation('agents')
  const language = i18n.language
  return useMemo(() => agentSettingsLabels(t, language), [t, language])
}
